import { inject, injectable } from "tsyringe";
import { of } from "rxjs";
import { CRM_ACTIONS } from "src/containers/crm/constants/actions";
import { CrmRepository } from "src/containers/crm/repositories/crm/crm-repository";
import { autoBind } from "src/shared/decorators/autobind";

type PolicyIdProp = string | number | undefined;
type ClaimNumberProp = string;

type PolicyInput = { systemCode: string; policyId: PolicyIdProp };
type PlanInput = { productCode: string };
export type CrmInput = PolicyInput | PlanInput;

@injectable()
export class CrmService {
    constructor(@inject("CrmRepository") private repo: CrmRepository) { }

    // Maps fields correctly for CRM service
    private getCorrectedInput(input: CrmInput) {
        return {
            systemCode: (input as PolicyInput).systemCode,
            policyId: (input as PolicyInput).policyId,
            product: (input as PlanInput).productCode,
        };
    }

    @autoBind
    trackHRTDownloadRejection(input: CrmInput) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_DOWNLOAD_REJECTION,
            ...this.getCorrectedInput(input),
        });
    }

    @autoBind
    trackHRTDownloadLiquidation(input: CrmInput) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_DOWNLOAD_LIQUIDATION,
            ...this.getCorrectedInput(input),
        });
    }

    @autoBind
    loginSuccess() {
        return of();
    }

    @autoBind
    _createUser() {
        return this.repo.saveTransaction({ action: CRM_ACTIONS.CREATE_USER });
    }

    @autoBind
    _changePassword() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.CHANGE_PASSWORD,
        });
    }

    @autoBind
    getAbcInsurance() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_ABC_INSURANCE,
        });
    }

    @autoBind
    listCoverages(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    _listClinics(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_CLINICS,
            systemCode,
            policyId,
        });
    }

    @autoBind
    _listGarages(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_GARAGES,
            systemCode,
            policyId,
        });
    }

    @autoBind
    claimDetail(claimNumber: ClaimNumberProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.CLAIM_DETAIL,
            claimNumber,
        });
    }

    /**
     * @param _systemCode Should follow the same rules for `policyId`
     * @param _policyId Should be *only* used with any policy that is **not** EPS
     * @param _productCode Should be *only* used with EPS plans
     */
    @autoBind
    getPolicyDetail(
        _systemCode?: string,
        _policyId?: PolicyIdProp,
        _productCode?: string
    ) {
        return of();
    }

    @autoBind
    listInsurances() {
        return of();
    }

    /**
     * @param systemCode Should follow the same rules for `policyId`
     * @param policyId Should be *only* used with any policy that is **not** EPS
     * @param productCode Should be *only* used with EPS plans
     */
    @autoBind
    downloadPolicy(
        _systemCode?: string | null,
        _policyId?: PolicyIdProp | null,
        _productCode?: string
    ) {
        return of();
    }

    @autoBind
    _listCrossPaymentCoupons() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_CROSS_PAYMENT_COUPONS,
        });
    }

    @autoBind
    listPaymentCoupons(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    getVoucher(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    getInsuranceActivation(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    downloadSoat(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    /**
     * @param _systemCode Should follow the same rules for `policyId`
     * @param _policyId Should be *only* used with any policy that is **not** EPS
     * @param _productCode Should be *only* used with EPS plans
     */
    @autoBind
    downloadCertificate(
        _systemCode: string,
        _policyId: PolicyIdProp,
        _productCode: string
    ) {
        return of();
    }

    @autoBind
    _downloadEecc(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_EECC,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadBullet(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    getPkTracking(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_PK_TRACKING,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadWellbeingMultiventajasPdf() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_MULTIVENTAJA,
        });
    }

    @autoBind
    getLifeBenefits() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_LIFE_BENEFITS,
        });
    }

    @autoBind
    getCarBenefits() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_CAR_BENEFITS,
        });
    }

    @autoBind
    downloadBallots(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    claimRequest(_systemCode: string, _policyId: PolicyIdProp) {
        return of();
    }

    @autoBind
    getHealthTravelerCarnetDetail(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_HEALTH_TRAVELER_CARNET_DETAIL,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadHealthTravelerCarnet(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_HEALTH_TRAVELER_CARNET,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getVeraWhatsappChat() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VERA_WHATSAPP_CHAT,
        });
    }

    @autoBind
    playScratchGame() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.PLAY_SCRATCH_GAME,
        });
    }

    @autoBind
    trackingDigitalRequest() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.TRACKING_DIGITAL_REQUEST,
        });
    }

    @autoBind
    medicineDeliveryRequestTracking() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.MEDICINE_DELIVERY_REQUEST_TRACKING,
        });
    }

    @autoBind
    getVehicleFines() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VEHICLE_FINES,
        });
    }

    @autoBind
    getVaccineCarnet() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VACCINE_CARNET,
        });
    }

    @autoBind
    vehicularAssistancesTrackRequest(
        action: CRM_ACTIONS,
        systemCode: string,
        policyId: PolicyIdProp
    ) {
        return this.repo.saveTransaction({
            action,
            systemCode,
            policyId,
        });
    }

    @autoBind
    accessElectionsOfficer() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.ELECTIONS_OFFICER,
        });
    }

    @autoBind
    coverNoteRequestTracking() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.COVER_NOTE_REQUEST_TRACKING,
        });
    }

    @autoBind
    healthReimbusementTrackingListing() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_LISTING,
        });
    }

    @autoBind
    accessLocateYourStation() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LOCATE_YOUR_STATION,
        });
    }

    @autoBind
    accessCocosYLucas() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.COCOS_Y_LUCAS,
        });
    }

    @autoBind
    accessPodcastEarthquake() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.PODCAST_EARTHQUAKE,
        });
    }

    @autoBind
    accessBenefitsVehicle() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.BENEFITS_VEHICLE,
        });
    }

    @autoBind
    accessReconocerte() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.BENEFITS_RECONOCERTE,
        });
    }
}
